/**
 * Bending Spoons SDK (Orion experiments + PicoX analytics) wiring for
 * listings-webapp. Mirrors the Valkyrie-app pattern documented in
 * `docs/orion-feature-flags.md` (creator-navigation PR #381):
 *
 *   - The provider is mounted once at the highest stable spot in the React
 *     tree (`pages/_app.page.tsx`).
 *   - This module owns the typed `Settings` shape for the whole app.
 *     One key per Orion flag, default = current production behavior.
 *   - Feature code never imports `useOrionSettings` directly; it imports a
 *     typed wrapper hook from this file (e.g. `useIsRelatedEventsDenseAdsEnabled`).
 *
 * To add a new flag: append one key + one default to `ListingsWebappSettings`
 * / `DEFAULT_SETTINGS`, then export a one-line wrapper hook below.
 */
import { useMemo } from 'react';
import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';

export type ListingsWebappSettings = {
  enable_increase_impressions_on_related_events: boolean;
  show_follow_venue_cta: boolean;
  show_follow_artist_cta: boolean;
  members_experiments_excluded_organizer_ids: string[];
  related_higher_price: boolean;
  enable_linear_score: boolean;
};

const DEFAULT_SETTINGS = createAppDefaults<ListingsWebappSettings>({
  enable_increase_impressions_on_related_events: false,
  show_follow_venue_cta: false,
  show_follow_artist_cta: false,
  members_experiments_excluded_organizer_ids: [],
  related_higher_price: false,
  enable_linear_score: false,
});

export const useBspConfig = () =>
  useMemo(() => {
    const domain = typeof window !== 'undefined' ? window.location.hostname : '';
    const environment = domain.includes('evbqa')
      ? 'staging'
      : domain.includes('localhost')
        ? 'development'
        : 'production';

    return {
      appName: 'listings-webapp',
      environment,
      defaultOrionSettings: DEFAULT_SETTINGS,
      defaultSegmentations: {},
    } as const;
  }, []);

/**
 * Gate for the denser related-events ad layout
 * (`enable_increase_impressions_on_related_events` Orion flag).
 *
 *   false (default / control) — request `slots: 2` and interleave ads via
 *     `interleaveArrays(promoted, organic)` (positions [1, 3]).
 *   true  (treatment)         — request `slots: 3` and interleave at
 *     positions [1, 2, 4] (ads → ads → organic → ads → organic).
 *
 * Same key + same default in eb-ui's `packages/attendee/listings/src/components/_shared`
 * and `packages/attendee/event-recommendations/src` so all three surfaces
 * ramp in lockstep.
 *
 * Excluded organizers: when `parentEventOrganizerId` is one of the ids in
 * the Orion setting `members_experiments_excluded_organizer_ids`, this
 * hook returns `false` regardless of the assigned segment — mirroring
 * the eb-ui checkout / event-recommendations gate so all three surfaces
 * opt the same events out in lockstep.
 */
export const useIsRelatedEventsDenseAdsEnabled = (parentEventOrganizerId?: string): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  const isOrganizerExcluded =
    !!parentEventOrganizerId && settings.members_experiments_excluded_organizer_ids.includes(parentEventOrganizerId);
  if (isOrganizerExcluded) {
    return false;
  }
  return settings.enable_increase_impressions_on_related_events;
};

/** Gate for the Follow Venue CTA (`show_follow_venue_cta` Orion flag). */
export const useShowFollowVenueCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_venue_cta;
};

/** Gate for the Follow Artist CTA (`show_follow_artist_cta` Orion flag). */
export const useShowFollowArtistCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_artist_cta;
};

/**
 * Gate for the higher-price related events experiment (`related_higher_price`
 * Orion flag). When on, the related-events request asks the search service to
 * zero out related events cheaper than the current event, so the carousel skews
 * towards equal-or-higher-priced events.
 *
 * Returns `isLoading` (unlike the other gates) so the related-events fetch can
 * wait for Orion to settle — otherwise a treatment visitor whose Orion resolves
 * after the fetch fires would be read as control and silently get the
 * unfiltered carousel, contaminating the experiment.
 */
export const useIsRelatedHigherPriceEnabled = (): { isEnabled: boolean; isLoading: boolean } => {
  let settings: Partial<ListingsWebappSettings> = {};
  let isLoading = false;
  try {
    // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    const state = useOrionSettings<ListingsWebappSettings>();
    settings = state.settings;
    isLoading = state.isLoading;
  } catch {}
  return { isEnabled: settings.related_higher_price ?? false, isLoading };
};

/**
 * Gate for the linear-ranker paid-boost experiment (`enable_linear_score` Orion
 * flag). When on, the related-events request asks the search service to rank
 * with the linear ranker (which carries the continuous paid-boost price signal)
 * via `score_mode: 'linear'`, instead of default multiplicative scoring.
 *
 * Same `enable_linear_score` key as the eb-ui discover surfaces
 * (`useIsLinearScoreEnabled`) and homepage-webapp, so every surface ramps in
 * lockstep off a single switch. Staff-gated server-side, so inert for non-staff
 * until switched on.
 *
 * Returns `isLoading` (like the higher-price gate) so the related-events fetch
 * can wait for Orion to settle before firing — otherwise a treatment visitor
 * whose Orion resolves after the fetch would be read as control and silently
 * get the default ranker, contaminating the experiment.
 */
export const useIsLinearScoreEnabled = (): { isEnabled: boolean; isLoading: boolean } => {
  let settings: Partial<ListingsWebappSettings> = {};
  let isLoading = false;
  try {
    // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    const state = useOrionSettings<ListingsWebappSettings>();
    settings = state.settings;
    isLoading = state.isLoading;
  } catch {}
  return { isEnabled: settings.enable_linear_score ?? false, isLoading };
};
